import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, differenceInDays, addDays } from 'date-fns';
import { CalendarIcon, Users, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { formatDateLong, formatDateShort, formatMonthName } from '../lib/dates';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { cn } from '../lib/utils';
import InfoTip from './InfoTip';
import { money } from '../lib/money';
import { isTooLateForCheckIn, minStayFor, isWithinMinStay } from '../lib/bookingRules';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingWidget = ({ variant = 'default', className = '' }) => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);
  const [guests, setGuests] = useState('2');
  const [blockedDates, setBlockedDates] = useState([]);
  // Ελάχιστη διαμονή ανά ημέρα άφιξης (από Smoobu/PriceLabs)
  const [minStays, setMinStays] = useState({});
  const [scarcity, setScarcity] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch blocked dates on mount
  useEffect(() => {
    const fetchBlockedDates = async () => {
      try {
        const response = await axios.get(`${API}/blocked-dates`);
        const dates = response.data.blocked_dates.map(d => new Date(d));
        setBlockedDates(dates);
        try {
          const sc = await axios.get(`${API}/scarcity`);
          if (sc.data && sc.data.show) setScarcity(sc.data);
        } catch (e) { /* ignore */ }
      } catch (error) {
        console.error('Error fetching blocked dates:', error);
      }
    };
    fetchBlockedDates();
    axios.get(`${API}/min-stays`)
      .then((res) => setMinStays(res.data.min_stays || {}))
      .catch((error) => console.error('Error fetching min stays:', error));
  }, []);

  // Calculate price when dates change
  useEffect(() => {
    const calculatePrice = async () => {
      if (checkIn && checkOut) {
        setIsLoading(true);
        try {
          const response = await axios.get(`${API}/availability`, {
            params: {
              check_in: format(checkIn, 'yyyy-MM-dd'),
              check_out: format(checkOut, 'yyyy-MM-dd'),
              guests: parseInt(guests),
            },
          });
          setPricing(response.data);
        } catch (error) {
          console.error('Error calculating price:', error);
          setPricing(null);
        }
        setIsLoading(false);
      }
    };
    calculatePrice();
  }, [checkIn, checkOut, guests]);

  const handleCheckAvailability = () => {
    // Έλεγχος διαθεσιμότητας πριν την ανακατεύθυνση
    if (checkIn && checkOut) {
      // Προθεσμία 6 ωρών: ίδια μέρα μόνο έως 10:00 ώρα Ελλάδας
      if (isTooLateForCheckIn(checkIn)) {
        toast.error(t('booking.sameDayError'));
        return;
      }
      // Ελάχιστη διαμονή για την ημέρα άφιξης
      const minStay = minStayFor(minStays, checkIn);
      if (differenceInDays(checkOut, checkIn) < minStay) {
        toast.error(t('booking.minStayError').replace('{n}', minStay));
        return;
      }
      let d = new Date(checkIn);
      while (d < checkOut) {
        if (isDateBlocked(d)) {
          toast.error(t('toasts.someDatesUnavailable'));
          return;
        }
        d = addDays(d, 1);
      }
    }
    const params = new URLSearchParams();
    if (checkIn) params.set('checkIn', format(checkIn, 'yyyy-MM-dd'));
    if (checkOut) params.set('checkOut', format(checkOut, 'yyyy-MM-dd'));
    params.set('guests', guests);
    navigate(`/booking?${params.toString()}`);
  };

  const isDateBlocked = (date) => {
    return blockedDates.some(
      (blocked) => format(blocked, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')
    );
  };

  // Μέγιστη επιτρεπτή αναχώρηση: η πρώτη κατειλημμένη ΝΥΧΤΑ μετά το check-in.
  // Η ημέρα αναχώρησης δεν είναι διανυκτέρευση (turnover day).
  const maxCheckOut = () => {
    if (!checkIn) return null;
    let d = new Date(checkIn);
    for (let i = 0; i < 400; i++) {
      if (isDateBlocked(d)) return d;
      d = addDays(d, 1);
    }
    return null;
  };

  const nights = checkIn && checkOut ? differenceInDays(checkOut, checkIn) : 0;

  // Compact hero variant
  if (variant === 'hero') {
    return (
      <div
        data-testid="booking-widget-hero"
        className={cn(
          'bg-white/95 backdrop-blur-md rounded-sm p-4 md:p-6 border border-[#D4A72C]/20',
          className
        )}
      >
        <div className="flex flex-col md:flex-row items-stretch gap-4">
          {/* Range Picker — Check-in + Check-out σε ένα */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid="booking-dates-trigger"
                className="flex-1 flex items-center gap-3 p-3 bg-[#f8fafc] rounded-sm border border-[#1E3A5F]/10 hover:border-[#D4A72C]/50 transition-colors text-left"
              >
                <CalendarIcon className="w-5 h-5 text-[#D4A72C]" />
                <div className="flex gap-4">
                  <div>
                    <p className="text-xs text-[#1E3A5F]/60 uppercase tracking-wider">{t('booking.checkIn')}</p>
                    <p className="text-sm font-medium text-[#1E3A5F]">
                      {checkIn ? formatDateLong(checkIn, language) : t('booking.selectDate')}
                    </p>
                  </div>
                  <div className="border-l border-[#1E3A5F]/10 pl-4">
                    <p className="text-xs text-[#1E3A5F]/60 uppercase tracking-wider">{t('booking.checkOut')}</p>
                    <p className="text-sm font-medium text-[#1E3A5F]">
                      {checkOut ? formatDateLong(checkOut, language) : t('booking.selectDate')}
                    </p>
                  </div>
                </div>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="range"
                numberOfMonths={2}
                selected={{ from: checkIn, to: checkOut }}
                onSelect={(range) => {
                  setCheckIn(range?.from || null);
                  // Αποδεκτό check-out μόνο αν καλύπτει την ελάχιστη διαμονή
                  if (range?.from && range?.to && range.to > range.from &&
                      differenceInDays(range.to, range.from) >= minStayFor(minStays, range.from)) {
                    setCheckOut(range.to);
                  } else {
                    setCheckOut(null);
                  }
                }}
                disabled={(date) => {
                  if (checkIn && !checkOut && date > checkIn) {
                    // Επιλογή check-out: όχι νωρίτερα από check-in + ελάχιστη διαμονή
                    if (isWithinMinStay(minStays, checkIn, date)) return true;
                    const mc = maxCheckOut();
                    if (mc && format(date, 'yyyy-MM-dd') === format(mc, 'yyyy-MM-dd')) return false;
                    return isDateBlocked(date);
                  }
                  // Check-in: παρελθόν ή σήμερα μετά τις 10:00 Ελλάδας κλειδωμένα
                  if (isTooLateForCheckIn(date)) return true;
                  return isDateBlocked(date);
                }}
                defaultMonth={checkIn || new Date()}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          {/* Guests */}
          <div className="flex-1 flex items-center gap-3 p-3 bg-[#f8fafc] rounded-sm border border-[#1E3A5F]/10">
            <Users className="w-5 h-5 text-[#D4A72C]" />
            <div className="flex-1">
              <p className="text-xs text-[#1E3A5F]/60 uppercase tracking-wider">{t('booking.guests')}</p>
              <Select value={guests} onValueChange={setGuests}>
                <SelectTrigger
                  data-testid="booking-guests-select"
                  className="border-0 p-0 h-auto shadow-none focus:ring-0 text-[#1E3A5F]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                    <SelectItem key={num} value={num.toString()}>
                      {num} {num === 1 ? t('booking.guest') : t('booking.guests')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* CTA Button */}
          <Button
            data-testid="booking-check-availability-btn"
            onClick={handleCheckAvailability}
            className="bg-[#1E3A5F] hover:bg-[#1E3A5F]/90 text-white px-8 py-6 btn-lift"
          >
            {t('booking.checkAvailability')}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Price Preview */}
        {pricing && pricing.available && nights > 0 && (
          <div className="mt-4 pt-4 border-t border-[#1E3A5F]/10 flex items-center justify-between">
            <div className="text-sm text-[#1E3A5F]/60">
              {nights} {t('booking.nights')} × {money(pricing.price_per_night, language)}
            </div>
            <div className="font-serif text-xl font-semibold text-[#1E3A5F]">
              {money(pricing.total, language)} <span className="text-sm font-normal text-[#1E3A5F]/60">{t('booking.total')}</span>
            </div>
          </div>
        )}
        
        {scarcity && (
          <p className="mt-3 text-xs text-center font-medium" style={{ color: '#C0392B' }}>
            {(scarcity.free_days === 1 ? t('booking.scarcityOne') : t('booking.scarcityMany').replace('{n}', scarcity.free_days))}{' '}
            {formatMonthName(scarcity.year, scarcity.month, language)}
          </p>
        )}
        {/* Mock system notice */}
        <p className="mt-3 text-xs text-center text-[#1E3A5F]/40 italic">
          {t('booking.enquiryNotice')}
        </p>
      </div>
    );
  }

  // Sticky mobile variant
  if (variant === 'sticky') {
    return (
      <div
        data-testid="booking-widget-sticky"
        className={cn(
          'fixed bottom-0 left-0 right-0 z-50 lg:hidden',
          'bg-white/95 backdrop-blur-md border-t border-[#D4A72C]/20 p-4',
          className
        )}
      >
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="font-serif text-lg font-semibold text-[#1E3A5F]">{t('booking.enquireNow')}</p>
            <p className="text-xs text-[#1E3A5F]/60">{t('booking.checkAvailabilityShort')}</p>
          </div>
          <Button
            data-testid="booking-sticky-btn"
            onClick={() => navigate('/booking')}
            className="bg-[#D4A72C] hover:bg-[#D4A72C]/90 text-[#1E3A5F] font-semibold btn-lift"
          >
            {t('booking.checkAvailability')}
          </Button>
        </div>
      </div>
    );
  }

  // Default sidebar variant
  return (
    <div
      data-testid="booking-widget-sidebar"
      className={cn(
        'bg-white rounded-sm border border-border shadow-lg p-6',
        className
      )}
    >
      <div className="space-y-4">
        {/* Price Header */}
        <div className="pb-4 border-b border-border">
          {pricing && pricing.available ? (
            <div className="flex items-baseline gap-2">
              <span className="font-serif text-2xl font-semibold">{money(pricing.price_per_night, language)}</span>
              <span className="text-muted-foreground text-sm">{t('booking.perNight')}</span>
              <InfoTip text={t('booking.priceInfo')} testId="sidebar-price-info" />
            </div>
          ) : (
            <div className="flex items-baseline gap-2">
              <span className="font-serif text-2xl font-semibold">{t('common.from')} {money(280, language)}</span>
              <span className="text-muted-foreground text-sm">{t('booking.perNight')}</span>
              <InfoTip text={t('booking.priceInfo')} testId="sidebar-price-info" />
            </div>
          )}
        </div>

        {/* Date Selection */}
        <div className="grid grid-cols-2 gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid="sidebar-checkin-trigger"
                className="flex flex-col items-start p-3 border border-border rounded-sm hover:border-primary/50 transition-colors"
              >
                <span className="text-xs text-muted-foreground uppercase tracking-wider">{t('booking.checkIn')}</span>
                <span className="text-sm font-medium">
                  {checkIn ? formatDateShort(checkIn, language) : '—'}
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={checkIn}
                onSelect={(date) => {
                  setCheckIn(date);
                  // Αυτόματο check-out στην ελάχιστη διαμονή αν το τρέχον δεν την καλύπτει
                  const minStay = minStayFor(minStays, date);
                  if (date && (!checkOut || differenceInDays(checkOut, date) < minStay)) {
                    setCheckOut(addDays(date, minStay));
                  }
                }}
                disabled={(date) => isTooLateForCheckIn(date) || isDateBlocked(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid="sidebar-checkout-trigger"
                className="flex flex-col items-start p-3 border border-border rounded-sm hover:border-primary/50 transition-colors"
              >
                <span className="text-xs text-muted-foreground uppercase tracking-wider">{t('booking.checkOut')}</span>
                <span className="text-sm font-medium">
                  {checkOut ? formatDateShort(checkOut, language) : '—'}
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={checkOut}
                onSelect={setCheckOut}
                disabled={(date) =>
                  date < (checkIn ? addDays(checkIn, minStayFor(minStays, checkIn)) : new Date()) ||
                  (maxCheckOut() ? date > maxCheckOut() : false)
                }
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Guests */}
        <div className="p-3 border border-border rounded-sm">
          <span className="text-xs text-muted-foreground uppercase tracking-wider block mb-1">{t('booking.guests')}</span>
          <Select value={guests} onValueChange={setGuests}>
            <SelectTrigger data-testid="sidebar-guests-select" className="border-0 p-0 h-auto shadow-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                <SelectItem key={num} value={num.toString()}>
                  {num} {num === 1 ? t('booking.guest') : t('booking.guests')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Price Breakdown */}
        {pricing && pricing.available && nights > 0 && (
          <div className="space-y-2 py-4 border-t border-border">
            {pricing.nightly_rates && pricing.nightly_rates.map((rate, index) => (
              <div key={index} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{rate.date}</span>
                <span>{money(rate.price, language)}</span>
              </div>
            ))}
            {pricing.length_discount_percent > 0 && (
              <div className="flex justify-between text-sm pt-1 border-t border-border/50">
                <span className="text-green-600">{t('booking.lengthDiscount')} (-{pricing.length_discount_percent}%)</span>
                <span className="text-green-600">✓</span>
              </div>
            )}
            {/* ΤΑΚΚ με ανάλυση: €/νύχτα × νύχτες = σύνολο */}
            <div data-testid="sidebar-env-fee-line" className="text-sm pt-1 border-t border-border/50">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t('booking.envFee')}
                  <InfoTip text={t('booking.envFeeInfo')} testId="sidebar-env-fee-info" />
                </span>
                <span>{money(pricing.env_fee_total, language)}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t('booking.envFeeShort')}: {money(pricing.env_fee_per_night, language)} × {pricing.nights}{' '}
                {pricing.nights === 1 ? t('booking.night') : t('booking.nights')} = {money(pricing.env_fee_total, language)}
              </p>
            </div>
            <div className="flex justify-between font-semibold pt-2 border-t border-border">
              <span>{t('booking.total')}</span>
              <span>{money(pricing.total, language)}</span>
            </div>
          </div>
        )}

        {/* CTA */}
        <Button
          data-testid="sidebar-book-btn"
          onClick={handleCheckAvailability}
          disabled={isLoading}
          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-6 btn-lift"
        >
          {isLoading ? 'Loading...' : t('booking.checkAvailability')}
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          {t('booking.wonNotChargedYet')}
        </p>
      </div>
    </div>
  );
};

export default BookingWidget;
